import type { PlcStRule } from './plcApi'

/**
 * FBD-style rendering of structured ST logic rules — synthesized layout, not
 * from the source file (ST carries no drawing). One row per rule: condition
 * boxes on the left feed an `&` gate (only when there are 2+ conditions),
 * which feeds the `target := expression` box. A `LOOP(...)` condition marks
 * iteration context and is drawn dashed to distinguish it from a boolean
 * guard.
 */

const BOX_H = 26
const ROW_GAP = 18
const COND_GAP = 8
const COND_W = 230
const GATE_W = 40
const EXPR_W = 320
const COL_GAP = 30
const PAD = 10
const FONT = 'ui-monospace, monospace'

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text

const isLoopCondition = (condition: string) => condition.startsWith('LOOP(')

interface Row {
  rule: PlcStRule
  top: number
  height: number
}

const StLogicView = ({ rules }: { rules: PlcStRule[] }) => {
  if (rules.length === 0) return null

  let cursor = PAD
  const rows: Row[] = rules.map((rule) => {
    const boxes = Math.max(1, rule.conditions.length)
    const height = boxes * BOX_H + (boxes - 1) * COND_GAP
    const row = { rule, top: cursor, height }
    cursor += height + ROW_GAP
    return row
  })

  const gateX = PAD + COND_W + COL_GAP
  const exprX = gateX + GATE_W + COL_GAP
  const width = exprX + EXPR_W + PAD
  const height = cursor - ROW_GAP + PAD

  return (
    <svg
      data-testid="st-logic-diagram"
      viewBox={`0 0 ${width} ${height}`}
      className="w-full text-foreground"
      style={{ maxWidth: `${width}px` }}
      role="img"
      aria-label="Derived logic diagram"
    >
      {rows.map(({ rule, top, height: rowHeight }, index) => {
        const midY = top + rowHeight / 2
        const hasGate = rule.conditions.length >= 2
        const exprText = truncate(`${rule.target} := ${rule.expression}`, 46)

        return (
          <g key={index}>
            {rule.conditions.map((condition, conditionIndex) => {
              const boxY = top + conditionIndex * (BOX_H + COND_GAP)
              const boxMidY = boxY + BOX_H / 2
              const joinX = hasGate ? gateX : exprX
              return (
                <g
                  key={conditionIndex}
                  data-testid={isLoopCondition(condition) ? 'st-loop-cond' : 'st-cond'}
                >
                  <rect
                    x={PAD}
                    y={boxY}
                    width={COND_W}
                    height={BOX_H}
                    rx={4}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.2}
                    strokeDasharray={isLoopCondition(condition) ? '5 3' : undefined}
                  />
                  <text
                    x={PAD + COND_W / 2}
                    y={boxMidY + 3.5}
                    textAnchor="middle"
                    fontSize={10}
                    fontFamily={FONT}
                    fill="currentColor"
                  >
                    <title>{condition}</title>
                    {truncate(condition, 36)}
                  </text>
                  <path
                    d={`M ${PAD + COND_W} ${boxMidY} L ${joinX - 8} ${boxMidY} L ${joinX - 8} ${midY} L ${joinX} ${midY}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.2}
                    opacity={0.75}
                  />
                </g>
              )
            })}

            {hasGate && (
              <g data-testid="st-and-gate">
                <rect
                  x={gateX}
                  y={midY - BOX_H / 2}
                  width={GATE_W}
                  height={BOX_H}
                  rx={4}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                />
                <text
                  x={gateX + GATE_W / 2}
                  y={midY + 4}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={600}
                  fontFamily={FONT}
                  fill="currentColor"
                >
                  &amp;
                </text>
                <line
                  x1={gateX + GATE_W}
                  y1={midY}
                  x2={exprX}
                  y2={midY}
                  stroke="currentColor"
                  strokeWidth={1.2}
                  opacity={0.75}
                />
              </g>
            )}

            <rect
              x={exprX}
              y={midY - BOX_H / 2}
              width={EXPR_W}
              height={BOX_H}
              rx={4}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
            />
            <text
              x={exprX + 10}
              y={midY + 3.5}
              fontSize={10}
              fontFamily={FONT}
              fill="currentColor"
            >
              <title>{`${rule.target} := ${rule.expression}`}</title>
              {exprText}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default StLogicView
